/**
 * SiteChrome — global page wrapper that reuses the SUPERTMATIK homepage
 * chrome (dark navy background, neon decor, sticky header with the logo /
 * brand text / language pill / SAIR, and the FAIXA statistics footer) on
 * EVERY page of the site.
 *
 *   <SiteChrome>
 *     <YourPageContent />
 *   </SiteChrome>
 *
 * The homepage config is fetched once on mount and shared with the
 * exported `HomeHeader` and `StatsFooter` components from Home.jsx so any
 * admin change to the homepage chrome instantly propagates across every
 * page — no duplicated CSS or configuration.
 */
import React, { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { fetchHomepageConfig, readHomepageConfigCacheSync } from "@/lib/homepageConfigCache";
import { HomeHeader, StatsFooter } from "@/pages/Home";

export default function SiteChrome({ children, mainClassName = "" }) {
  const { lang } = useI18n();
  // Paint the site chrome in the SAME frame as the intro splash on F5.
  const [cfg, setCfg] = useState(() => readHomepageConfigCacheSync());

  useEffect(() => {
    let mounted = true;
    fetchHomepageConfig()
      .then((data) => { if (mounted) setCfg(data); })
      .catch(() => { if (mounted) setCfg({}); });
    return () => { mounted = false; };
  }, []);

  const theme = cfg?.site_theme || {};
  const bg1 = theme.bg_color || "#04081F";
  const bg2 = theme.bg_color_alt || "#0B1445";

  // Light-background utility classes whose descendants must KEEP the normal
  // dark-on-light muted greys — i.e. cards / boxes / callouts. Extend this
  // list whenever a new light background token is introduced (e.g. add
  // `bg-brand-bg`, `bg-brand-bg2` for the custom brand palette used by the
  // Eliminatorias landing / VS Humano lobby / OutboxStatusBadge, etc).
  const LIGHT_BGS = [
    "bg-white",
    "bg-brand-bg", "bg-brand-bg2",       // #FFFFFF and #F8FAFC (custom brand)
    "bg-slate-50", "bg-slate-100",
    "bg-gray-50", "bg-gray-100",
    "bg-zinc-50", "bg-zinc-100",
    "bg-neutral-50", "bg-neutral-100",
    "bg-stone-50", "bg-stone-100",
    "bg-amber-50", "bg-amber-100",
    "bg-yellow-50", "bg-yellow-100",
    "bg-emerald-50", "bg-emerald-100",
    "bg-teal-50", "bg-teal-100",
    "bg-sky-50", "bg-sky-100",
    "bg-cyan-50", "bg-cyan-100",
    "bg-blue-50", "bg-blue-100",
    "bg-indigo-50", "bg-indigo-100",
    "bg-violet-50", "bg-violet-100",
    "bg-purple-50", "bg-purple-100",
    "bg-pink-50", "bg-pink-100",
    "bg-rose-50", "bg-rose-100",
    // Light gradients — Tailwind renders these via `background-image` on
    // the same element, so we also treat any container whose gradient
    // STARTS from a "-50" or "-100" tint as a light-bg wrapper. This keeps
    // cards like the VS Robot leaderboard (from-amber-50 via-orange-50
    // to-rose-50) rendering dark text instead of the dark-chrome light gray.
    "from-slate-50", "from-slate-100",
    "from-gray-50", "from-gray-100",
    "from-zinc-50", "from-zinc-100",
    "from-neutral-50", "from-neutral-100",
    "from-stone-50", "from-stone-100",
    "from-amber-50", "from-amber-100",
    "from-yellow-50", "from-yellow-100",
    "from-orange-50", "from-orange-100",
    "from-emerald-50", "from-emerald-100",
    "from-teal-50", "from-teal-100",
    "from-sky-50", "from-sky-100",
    "from-cyan-50", "from-cyan-100",
    "from-blue-50", "from-blue-100",
    "from-indigo-50", "from-indigo-100",
    "from-violet-50", "from-violet-100",
    "from-purple-50", "from-purple-100",
    "from-pink-50", "from-pink-100",
    "from-rose-50", "from-rose-100",
  ];
  const lightBgSelector = LIGHT_BGS.map((c) => `.${c}`).join(", ");

  return (
    <div
      className="min-h-screen relative site-chrome"
      style={{ background: `linear-gradient(180deg, ${bg1} 0%, ${bg2} 60%, ${bg1} 100%)` }}
      data-testid="site-chrome"
    >
      <style>{`
        /* -------------------------------------------------------------
           TEXT CONTRAST INSIDE THE DARK CHROME
           -------------------------------------------------------------
           Every page rendered inside SiteChrome sits on a very dark navy
           background. Many inner components use Tailwind muted grays
           (text-slate-500..900, text-gray-500..900, text-zinc-*, etc.)
           which on that dark background become barely legible.
           These rules FORCE muted text classes to render in a light shade
           when they are NOT inside an explicit light-background container
           (bg-white / bg-slate-50 / any of the well-known Tailwind
           light-tint utilities). Inside light-bg containers, the original
           muted greys stay readable and are kept unchanged. Homepage,
           header and footer are outside main so they are untouched. */
        .site-chrome > main { color: #E2E8F0; }

        /* Boost muted text to slate-200 (#E2E8F0) or slate-300 (#CBD5E1)
           on dark backgrounds. Keeps intentional coloured text (emerald,
           sky, amber, rose, indigo, etc.) untouched. */
        .site-chrome > main .text-slate-400,
        .site-chrome > main .text-slate-500,
        .site-chrome > main .text-slate-600,
        .site-chrome > main .text-slate-700,
        .site-chrome > main .text-slate-800,
        .site-chrome > main .text-slate-900,
        .site-chrome > main .text-gray-400,
        .site-chrome > main .text-gray-500,
        .site-chrome > main .text-gray-600,
        .site-chrome > main .text-gray-700,
        .site-chrome > main .text-gray-800,
        .site-chrome > main .text-gray-900,
        .site-chrome > main .text-zinc-500,
        .site-chrome > main .text-zinc-600,
        .site-chrome > main .text-zinc-700,
        .site-chrome > main .text-zinc-800,
        .site-chrome > main .text-zinc-900,
        .site-chrome > main .text-neutral-500,
        .site-chrome > main .text-neutral-600,
        .site-chrome > main .text-neutral-700,
        .site-chrome > main .text-neutral-800,
        .site-chrome > main .text-neutral-900,
        .site-chrome > main .text-stone-500,
        .site-chrome > main .text-stone-600,
        .site-chrome > main .text-stone-700,
        .site-chrome > main .text-stone-800,
        .site-chrome > main .text-stone-900,
        .site-chrome > main .text-brand-muted,
        .site-chrome > main .text-brand-text,
        .site-chrome > main .text-brand-secondary { color: #E2E8F0 !important; }

        /* Even lighter for labels/placeholders (text-slate-400 area) so
           they don't blend into the darker mid-tones. */
        .site-chrome > main .text-slate-400,
        .site-chrome > main .text-gray-400,
        .site-chrome > main .text-zinc-400,
        .site-chrome > main .text-neutral-400,
        .site-chrome > main .text-stone-400 { color: #CBD5E1 !important; }

        /* Inside LIGHT-BACKGROUND containers (cards / boxes / etc.), the
           original muted greys ARE readable and must be preserved. This
           reverts the overrides above by rewinding to the standard
           Tailwind hex values. The whitelist of light background classes
           lives in JS above (LIGHT_BGS) so it stays DRY and easy to
           extend when a new light background token is introduced.
           NOTE: per product decision, ANY grey shade on a light background
           is bumped DARKER (slate-700+ range) or fully black, so the copy
           reads with maximum contrast regardless of the original class. */
        .site-chrome > main :is(${lightBgSelector}) { color: #0F172A; }

        .site-chrome > main :is(${lightBgSelector}) .text-slate-300 { color: #334155 !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-slate-400 { color: #334155 !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-slate-500 { color: #1E293B !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-slate-600 { color: #1E293B !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-slate-700 { color: #0F172A !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-slate-800 { color: #0F172A !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-slate-900 { color: #0F172A !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-gray-300,
        .site-chrome > main :is(${lightBgSelector}) .text-gray-400 { color: #334155 !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-gray-500,
        .site-chrome > main :is(${lightBgSelector}) .text-gray-600 { color: #1E293B !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-gray-700,
        .site-chrome > main :is(${lightBgSelector}) .text-gray-800,
        .site-chrome > main :is(${lightBgSelector}) .text-gray-900 { color: #0F172A !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-zinc-300,
        .site-chrome > main :is(${lightBgSelector}) .text-zinc-400 { color: #334155 !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-zinc-500,
        .site-chrome > main :is(${lightBgSelector}) .text-zinc-600 { color: #1E293B !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-zinc-700,
        .site-chrome > main :is(${lightBgSelector}) .text-zinc-800,
        .site-chrome > main :is(${lightBgSelector}) .text-zinc-900 { color: #0F172A !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-neutral-300,
        .site-chrome > main :is(${lightBgSelector}) .text-neutral-400 { color: #334155 !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-neutral-500,
        .site-chrome > main :is(${lightBgSelector}) .text-neutral-600 { color: #1E293B !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-neutral-700,
        .site-chrome > main :is(${lightBgSelector}) .text-neutral-800,
        .site-chrome > main :is(${lightBgSelector}) .text-neutral-900 { color: #0F172A !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-stone-300,
        .site-chrome > main :is(${lightBgSelector}) .text-stone-400 { color: #334155 !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-stone-500,
        .site-chrome > main :is(${lightBgSelector}) .text-stone-600 { color: #1E293B !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-stone-700,
        .site-chrome > main :is(${lightBgSelector}) .text-stone-800,
        .site-chrome > main :is(${lightBgSelector}) .text-stone-900 { color: #0F172A !important; }
        /* Custom brand text tokens — re-darken them on light-bg containers
           so labels ("Username", "Password") and the "Como funciona" step
           text render in dark navy rather than the dark-mode light gray. */
        .site-chrome > main :is(${lightBgSelector}) .text-brand-text { color: #0F172A !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-brand-muted { color: #1E293B !important; }
        .site-chrome > main :is(${lightBgSelector}) .text-brand-secondary { color: #10B981 !important; }
      `}</style>
      {cfg && <HomeHeader cfg={cfg} lang={lang} />}
      <main className={`relative z-10 pt-6 md:pt-8 ${mainClassName}`}>{children}</main>
      {cfg && <StatsFooter cfg={cfg} lang={lang} />}
    </div>
  );
}
